import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

// ---------------------------------------------------------------------------
// Field key enum
// ---------------------------------------------------------------------------
const enum fields {
    authorImage = "authorImage",
    authorName = "authorName",
    authorRole = "authorRole",
    authorSpecialties = "authorSpecialties",
    authorEmail = "authorEmail",
    authorLinkedIn = "authorLinkedIn",
    authorBio = "authorBio",
    authorEducation = "authorEducation",
    authorExperience = "authorExperience",
    picksLabel = "picksLabel",
    picksCards = "picksCards",
    // Per-card sub-fields (used inside the ListField)
    pickTitle = "pickTitle",
    pickAuthor = "pickAuthor",
    pickAuthorImage = "pickAuthorImage",
    pickLink = "pickLink",
}

// ---------------------------------------------------------------------------
// List item model
// ---------------------------------------------------------------------------
interface PickItem {
    pickTitle: string;
    pickAuthor: string;
    pickAuthorImage: string;
    pickLink: string;
}

interface PickCardModel {
    fields: PickItem;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface OwnProps extends WidgetProps {
    fields: {
        [fields.authorImage]: string;
        [fields.authorName]: string;
        [fields.authorRole]: string;
        [fields.authorSpecialties]: string;
        [fields.authorEmail]: string;
        [fields.authorLinkedIn]: string;
        [fields.authorBio]: string;
        [fields.authorEducation]: string;
        [fields.authorExperience]: string;
        [fields.picksLabel]: string;
        [fields.picksCards]: PickCardModel[];
    };
}

// ---------------------------------------------------------------------------
// Style interface
// ---------------------------------------------------------------------------
export interface CENAuthorProfileStyles {
    pageWrapper?: InjectableCss;
    // Header
    headerWrapper?: InjectableCss;
    avatarWrapper?: InjectableCss;
    headerInfoWrapper?: InjectableCss;
    authorName?: InjectableCss;
    authorRole?: InjectableCss;
    socialRow?: InjectableCss;
    socialIcon?: InjectableCss;
    // Divider
    divider?: InjectableCss;
    // Bio
    bioSection?: InjectableCss;
    bioText?: InjectableCss;
    bioSidebar?: InjectableCss;
    sidebarLabel?: InjectableCss;
    sidebarValue?: InjectableCss;
    sidebarRow?: InjectableCss;
    // Picks
    picksTitle?: InjectableCss;
    picksGrid?: InjectableCss;
    pickCard?: InjectableCss;
    pickCardTitle?: InjectableCss;
    pickCardAuthorWrapper?: InjectableCss;
    pickAuthorPip?: InjectableCss;
    pickAuthorName?: InjectableCss;
    pickCardFooter?: InjectableCss;
    readMoreLink?: InjectableCss;
}

// ---------------------------------------------------------------------------
// Default styles
// ---------------------------------------------------------------------------
export const CENAuthorProfileDefaultStyles: CENAuthorProfileStyles = {
    pageWrapper: {
        css: css`
            padding: 24px 20px 40px;
            font-size: 14px;
            line-height: 1.6;
            margin: 0 auto;
        `,
    },
    headerWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            gap: 30px;
            margin-bottom: 28px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    avatarWrapper: {
        css: css`
            width: 200px;
            overflow: hidden;
            flex-shrink: 0;
            img {
                width: 100%;
                height: auto;
            }
        `,
    },
    headerInfoWrapper: {
        css: css`
            padding-top: 8px;
        `,
    },
    authorName: {
        css: css`
            font-size: 30px;
            font-weight: 500;
            line-height: 1.2;
            margin-bottom: 4px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 22px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    authorRole: {
        css: css`
            font-size: 13px;
            color: #666;
            margin-bottom: 12px;
        `,
    },
    socialRow: {
        css: css`
            display: flex;
            gap: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                    `,
                    css`
                        grid-template-columns: 1fr;
                    `,
                    css`
                        grid-template-columns: 1fr;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    socialIcon: {
        css: css`
            width: 26px;
            height: 26px;
            border-radius: 4px;
            border: 1px solid #ddd;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            background: #f5f5f5;
            svg {
                width: 13px;
                height: 13px;
                color: #555;
            }
            &:hover {
                background: #ebebeb;
            }
        `,
    },
    divider: {
        css: css`
            border: none;
            border-top: 1px solid #e5e5e5;
            margin: 0 0 22px;
        `,
    },
    bioSection: {
        css: css`
            display: grid;
            grid-template-columns: 3fr 2fr;
            gap: 24px;
            margin-bottom: 32px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 1fr;
                    `,
                    null,
                    css`
                        grid-template-columns: 1fr;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    bioText: {
        css: css`
            font-size: 13px;
            line-height: 1.75;
        `,
    },
    bioSidebar: {
        css: css`
            background: #f8f8f8;
            border-radius: 6px;
            padding: 14px 16px;
            font-size: 12.5px;
            line-height: 1.6;
        `,
    },
    sidebarRow: {
        css: css`
            margin-bottom: 12px;
            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    sidebarLabel: {
        css: css`
            font-weight: 600;
            font-size: 12.5px;
        `,
    },
    sidebarValue: {
        css: css`
            font-size: 12.5px;
        `,
    },
    picksTitle: {
        css: css`
            font-size: 20px;
            font-weight: 500;
            margin-bottom: 14px;
        `,
    },
    picksGrid: {
        css: css`
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 1fr;
                    `,
                    null,
                    css`
                        grid-template-columns: repeat(2, 1fr);
                    `,
                    null,
                    null,
                ])}
        `,
    },
    pickCard: {
        css: css`
            border: 1px solid #e5e5e5;
            border-radius: 8px;
            padding: 14px 16px 12px;
            display: flex;
            flex-direction: column;
            gap: 10px;
            background: #fff;
        `,
    },
    pickCardTitle: {
        css: css`
            font-size: 13px;
            font-weight: 600;
            line-height: 1.5;
            flex: 1;
        `,
    },
    pickCardAuthorWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 8px;
        `,
    },
    pickAuthorPip: {
        css: css`
            width: 24px;
            height: 24px;
            border-radius: 50%;
            overflow: hidden;
            flex-shrink: 0;
            background: #d6e8f8;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8px;
            font-weight: 600;
            color: #1a6aab;
            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                border-radius: 50%;
            }
        `,
    },
    pickAuthorName: {
        css: css`
            font-size: 11px;
            color: #666;
        `,
    },
    pickCardFooter: {
        css: css`
            border-top: 1px solid #e5e5e5;
            padding-top: 10px;
            text-align: right;
        `,
    },
    readMoreLink: {
        css: css`
            font-size: 12px;
            color: #1a6aab;
            cursor: pointer;
            text-decoration: none;
            &:hover {
                text-decoration: underline;
            }
        `,
    },
};

// ---------------------------------------------------------------------------
// Helpers 
// ---------------------------------------------------------------------------
const getInitials = (name: string): string =>
    name
        .split(" ")
        .map(w => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);

interface PickCardProps {
    title: string;
    authorName: string;
    authorImage: string;
    link: string;
    styles: CENAuthorProfileStyles;
}

const PickCard: FC<PickCardProps> = ({ title, authorName, authorImage, link, styles }) => {
    if (!title) return null;
    return (
        <StyledWrapper {...styles.pickCard}>
            <Typography variant="p" {...styles.pickCardTitle}>
                {title}
            </Typography>
            {/* <StyledWrapper {...styles.pickCardAuthorWrapper}>
                <StyledWrapper {...styles.pickAuthorPip}>
                    {authorImage ? (
                        <Img src={authorImage} altText={authorName} loading="lazy" />
                    ) : (
                        getInitials(authorName || "")
                    )}
                </StyledWrapper>
                <Typography variant="p" {...styles.pickAuthorName}>
                    by {authorName}
                </Typography>
            </StyledWrapper> */}
            <StyledWrapper {...styles.pickCardFooter}>
                <Link href={link || "#"} {...styles.readMoreLink}>
                    {siteMessage(" Read more")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const styles = CENAuthorProfileDefaultStyles;
type Props = OwnProps;

export const CENAuthorProfile: FC<Props> = ({ fields: f }) => {
    const hasEmail = !!f[fields.authorEmail];
    const hasLinkedIn = !!f[fields.authorLinkedIn];
    const picksCards: PickCardModel[] = f[fields.picksCards] || [];

    return (
        <StyledWrapper {...styles.pageWrapper}>
            {/* ── Header ── */}
            <StyledWrapper {...styles.headerWrapper}>
                {f.authorImage && (
                    <StyledWrapper {...styles.avatarWrapper}>
                        <Img src={f[fields.authorImage]} altText={f[fields.authorName]} loading="lazy" />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.headerInfoWrapper}>
                    <Typography variant="h1" {...styles.authorName}>
                        {translate("{0}", f[fields.authorName])}
                    </Typography>
                    <Typography variant="p" {...styles.authorRole}>
                        {f[fields.authorRole]}
                        {f[fields.authorSpecialties] && <>&nbsp;|&nbsp;{f[fields.authorSpecialties]}</>}
                    </Typography>
                    <StyledWrapper {...styles.socialRow}>
                        {hasEmail && (
                            <a href={`mailto:${f[fields.authorEmail]}`} title="Email" aria-label="Email author">
                                <StyledWrapper {...styles.socialIcon}>
                                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                                        <rect x="2" y="4" width="12" height="9" rx="1.5" />
                                        <path d="M2 6l6 4 6-4" />
                                    </svg>
                                </StyledWrapper>
                            </a>
                        )}
                        {hasLinkedIn && (
                            <a
                                href={f[fields.authorLinkedIn]}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="LinkedIn"
                                aria-label="LinkedIn profile"
                            >
                                <StyledWrapper {...styles.socialIcon}>
                                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
                                        <rect x="2" y="2" width="12" height="12" rx="2.5" />
                                        <circle cx="5.5" cy="5.5" r="0.8" fill="currentColor" stroke="none" />
                                        <path
                                            d="M5.5 7.5v4M8 11.5V9c0-1 .7-1.5 1.5-1.5S11 8 11 9v2.5"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </StyledWrapper>
                            </a>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            {/* ── Divider ── */}
            <StyledWrapper {...styles.divider} as="hr" />

            {/* ── Bio Section ── */}
            <StyledWrapper {...styles.bioSection}>
                <Typography variant="p" {...styles.bioText}>
                    {parse(f[fields.authorBio], parserOptions)}
                </Typography>
                <StyledWrapper {...styles.bioSidebar}>
                    {f[fields.authorEducation] && (
                        <StyledWrapper {...styles.sidebarRow}>
                            <Typography variant="p" {...styles.sidebarLabel}>
                                {siteMessage(" Education:")}
                            </Typography>
                            <Typography variant="p" {...styles.sidebarValue}>
                                {f[fields.authorEducation]}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {f[fields.authorExperience] && (
                        <StyledWrapper {...styles.sidebarRow}>
                            <Typography variant="p" {...styles.sidebarLabel}>
                                {siteMessage(" Previous experience:")}{" "}
                            </Typography>
                            <Typography variant="p" {...styles.sidebarValue}>
                                {f[fields.authorExperience]}
                            </Typography>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>

            {/* ── Picks Section ── */}
            {picksCards.length > 0 && (
                <>
                    <Typography variant="h2" {...styles.picksTitle}>
                        {f[fields.picksLabel] || `${f[fields.authorName].split(" ")[0]}'s picks`}
                    </Typography>
                    <StyledWrapper {...styles.picksGrid}>
                        {picksCards.map((card, index) => (
                            <PickCard
                                key={index}
                                title={card.fields.pickTitle}
                                authorName={card.fields.pickAuthor}
                                authorImage={card.fields.pickAuthorImage}
                                link={card.fields.pickLink}
                                styles={styles}
                            />
                        ))}
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

// ---------------------------------------------------------------------------
// Widget module definition
// ---------------------------------------------------------------------------
const AuthorProfileWidget: WidgetModule = {
    component: CENAuthorProfile,
    definition: {
        group: "Basic",
        displayName: "CENAuthorProfile",
        fieldDefinitions: [
            // ── Author identity ────────────────────────────────────────────
            {
                name: fields.authorImage,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Author Photo",
            },
            {
                name: fields.authorName,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "Author Name",
            },
            {
                name: fields.authorRole,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "Role / Title",
            },
            {
                name: fields.authorSpecialties,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "Specialties (shown after pipe)",
            },
            // ── Social links ───────────────────────────────────────────────
            {
                name: fields.authorEmail,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "Email Address",
            },
            {
                name: fields.authorLinkedIn,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "LinkedIn URL",
            },
            // ── Bio content ────────────────────────────────────────────────
            {
                name: fields.authorBio,
                fieldType: "Translatable",
                editorTemplate: "RichTextField",
                defaultValue: "",
                displayName: "Bio / Description",
            },
            {
                name: fields.authorEducation,
                fieldType: "Translatable",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                displayName: "Education",
            },
            {
                name: fields.authorExperience,
                fieldType: "Translatable",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                displayName: "Previous Experience",
            },
            // ── Picks section ──────────────────────────────────────────────
            {
                name: fields.picksLabel,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "Picks Section Label (leave blank for auto)",
            },
            {
                name: fields.picksCards,
                fieldType: "General",
                editorTemplate: "ListField",
                defaultValue: [],
                displayName: "Picks",
                getDisplay: (item: HasFields) => (item.fields["pickTitle"] as string) || "Untitled Pick",
                fieldDefinitions: [
                    {
                        name: fields.pickTitle,

                        editorTemplate: "TextField",
                        defaultValue: "",
                        displayName: "Blog Title",
                    },
                    // {
                    //     name: fields.pickAuthor,

                    //     editorTemplate: "TextField",
                    //     defaultValue: "",
                    //     displayName: "Author Name",
                    // },
                    // {
                    //     name: fields.pickAuthorImage,

                    //     editorTemplate: "ImagePickerField",
                    //     defaultValue: "",
                    //     displayName: "Author Photo",
                    // },
                    {
                        name: fields.pickLink,

                        editorTemplate: "TextField",
                        defaultValue: "",
                        displayName: "Link URL",
                    },
                ],
            },
        ],
    },
};

export default AuthorProfileWidget;
